import { searchWhisper } from "@dataone/lib";
import { useMutation } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { styled } from "styled-components";
import { Tooltip } from "antd";
import { MicroIcon, ThreeDotsIcon } from "@dataone/icons";

interface SearchAudioProps {
  isChatActive?: boolean;
  recordingStatus: boolean;
  setRecordingStatus: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  onWhisperSuccess: (transcript: string) => void;
  handleMicrophonePermission?: () => void;
  disabled?: boolean;
}

const SearchAudio = ({
  isChatActive,
  recordingStatus,
  setRecordingStatus,
  onWhisperSuccess,
  handleMicrophonePermission,
  disabled,
}: SearchAudioProps) => {
  const [isAudioSupported, setAudioSupported] = useState(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<any>();

  const { mutate: whisper } = useMutation(searchWhisper);

  useEffect(() => {
    if (!isAudioSupported) checkMicrophoneSupported();
    if (recordingStatus) startRecording();
    else {
      mediaRecorder.current && mediaRecorder.current.stop();
      clearRecordingTimeout();
    }
    return () => {
      clearRecordingTimeout();
    };
  }, [recordingStatus]);

  const checkMicrophoneSupported = async () => {
    if ("MediaRecorder" in window) {
      setAudioSupported(true);
    } else {
      alert("The MediaRecorder API is not supported in your browser.");
    }
  };

  const startRecording = async () => {
    try {
      const currentStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      if (currentStream) {
        // Stop existing stream if it exists
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        setStream(currentStream);
        const media = new MediaRecorder(currentStream);
        mediaRecorder.current = media;
        const localAudioChunks: Blob[] = [];

        mediaRecorder.current.ondataavailable = (event: any) => {
          if (typeof event.data === "undefined") return;
          if (event.data.size === 0) return;
          localAudioChunks.push(event.data);
        };

        mediaRecorder.current.onstop = () => {
          const audioBlob = new Blob(localAudioChunks, { type: "audio/webm" });
          const fileName =
            URL.createObjectURL(audioBlob).split("/").at(-1) + ".webm";
          confirmWhisper(audioBlob, fileName);
        };
        mediaRecorder.current.start();

        timeoutRef.current = setTimeout(() => {
          if (
            mediaRecorder.current &&
            mediaRecorder.current.state === "recording"
          )
            setRecordingStatus(false);
        }, 20000);
      }
    } catch (err: any) {
      alert((err as Error).message);
    }
  };

  const clearRecordingTimeout = () => {
    timeoutRef.current && clearTimeout(timeoutRef.current);
    stream && stream.getTracks().forEach((track) => track.stop());
  };

  const confirmWhisper = (audio: Blob, filename: string) => {
    setLoading(true);
    whisper(
      { audio, filename },
      {
        onSuccess: ({ transcript }) => {
          onWhisperSuccess(transcript);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }
    );
  };

  return (
    <StyledSearchAudio>
      {!recordingStatus ? (
        isChatActive && !loading ? (
          <StyldeMicroIcon onClick={handleMicrophonePermission}>
            <MicroIcon />
          </StyldeMicroIcon>
        ) : loading ? (
          <LoadingOutlined />
        ) : (
          <Tooltip
            placement="top"
            title={<span style={{ fontSize: "12px" }}>Voice Search</span>}
            overlayStyle={{ maxWidth: "300px" }}
          >
            <StyledIcon
              onClick={handleMicrophonePermission}
              disabled={disabled}
            >
              <MicroIcon />
            </StyledIcon>
          </Tooltip>
        )
      ) : (
        <StyledSoundAudio onClick={() => setRecordingStatus(false)}>
          <ThreeDotsIcon />
        </StyledSoundAudio>
      )}
    </StyledSearchAudio>
  );
};
export default SearchAudio;

const StyledSearchAudio = styled.div`
  > img {
    cursor: pointer;
    margin-top: 7px;
  }
  .anticon-loading {
    font-size: 23px;
    margin-right: 10px;
  }
`;

const StyldeMicroIcon = styled.div`
  display: flex;
  font-size: 20px;
  color: #1a52fb;
  cursor: pointer;
`;

const StyledSoundAudio = styled.div`
  display: flex;
  align-items: center;
  transform: rotate(90deg);
  padding: 6px;
  font-size: 12px;
  border-radius: 50%;
  background: #1a52fb;
  cursor: pointer;
  color: #fff;
`;

const StyledIcon = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #000;
  border-radius: 50%;
  border: 1px solid #b6c8ff;
  padding: 7px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? "0.7" : "1")};
`;
